import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type CreateWorkOrderInput,
	patchWorkOrderLinks,
	type UpdateWorkOrderInput,
	type WorkOrder,
	type WorkOrderDetail,
} from '@/domain/work-order.types'
import { applyPackPatch } from '@/domain/work-order-pack'
import { invalidateClassifiedReads } from '@/hooks/useClassification'
import {
	type GenerationContextInput,
	type WorkOrderBoardPage,
	type WorkOrderDetailRead,
	type WorkOrderListParams,
	workOrderApi,
} from '@/services/work-order.service'
import { MESSAGES_KEY } from './useMessages'
import { WORK_ORDER_MESSAGES_KEY } from './useWorkOrderMessages'
import { invalidateWorkOrderActivity } from './useWorkOrderTimeline'

/** React-query hooks for the work-order board and page. */

/**
 * The board page query. `useWorkOrderBoard` and `useWorkOrders` share this key,
 * so the board's workflow columns and the plain order list come from one fetch.
 */
const boardQuery = (params?: WorkOrderListParams) => ({
	queryKey: ['work-orders', params ?? {}],
	queryFn: () => workOrderApi.list(params),
})

/** The full board page: work orders plus the workflows and statuses to render them under. */
export const useWorkOrderBoard = (params?: WorkOrderListParams, options?: { enabled?: boolean }) =>
	useQuery({ ...boardQuery(params), enabled: options?.enabled })

/**
 * One page of the work-order list. Shares the board's key family, so every
 * invalidation and optimistic patch of `['work-orders']` reaches it, and keeps
 * the previous page on screen while the next one loads.
 */
export const useWorkOrderList = (params: WorkOrderListParams, options?: { enabled?: boolean }) =>
	useQuery({ ...boardQuery(params), enabled: options?.enabled, placeholderData: keepPreviousData })

/** The statuses a work-order list can be filtered by, one per code across the tenant's workflows. */
export const useWorkOrderStatuses = () =>
	useQuery({ queryKey: ['work-order-statuses'], queryFn: workOrderApi.statuses, staleTime: 5 * 60_000 })

/**
 * Just the work orders — for the surfaces that do not render workflow columns.
 * `options.enabled` lets a caller hold the fetch until its scoping input is
 * known (the review queue waits for the signed-in user's id).
 */
export const useWorkOrders = (params?: WorkOrderListParams, options?: { enabled?: boolean }) =>
	useQuery({
		...boardQuery(params),
		enabled: options?.enabled,
		select: (page: WorkOrderBoardPage) => page.workOrders,
	})

/**
 * The single work-order read. `useWorkOrder` and `useWorkOrderWorkflow` share
 * this key, so the order and its assigned workflow configuration come from one
 * fetch — the detail surfaces never consult a board page for configuration.
 */
const detailQuery = (id: string | null) => ({
	queryKey: ['work-order', id],
	queryFn: () => workOrderApi.get(id as string),
	enabled: !!id,
})

export const useWorkOrder = (id: string | null) =>
	useQuery({ ...detailQuery(id), select: (detail: WorkOrderDetailRead) => detail.workOrder })

/**
 * The order's assigned workflow with its active statuses, for surfaces that must
 * resolve a transition target against configured membership. `undefined` while
 * the read is in flight; `null` once loaded and the order has no resolvable
 * workflow — the two are distinct, and neither may be treated as a status set.
 */
export const useWorkOrderWorkflow = (id: string | null) =>
	useQuery({ ...detailQuery(id), select: (detail: WorkOrderDetailRead) => detail.workflow })

/**
 * Legal transitions + guards + lane for the state timeline/action bar
 * (design.md D7) — shares `useWorkOrder`/`useWorkOrderWorkflow`'s cache key,
 * so all three come from the one `GET /work-orders/{id}` fetch.
 */
export const useWorkOrderWorkflowSnapshot = (id: string | null) =>
	useQuery({ ...detailQuery(id), select: (detail: WorkOrderDetailRead) => detail.workflowSnapshot })

export const useWorkOrderContext = (id: string | null) =>
	useQuery({
		queryKey: ['work-order-context', id],
		queryFn: () => workOrderApi.context(id as string),
		enabled: !!id,
	})

const generationContextKey = (id: string | null) => ['work-order-generation-context', id] as const

export const useGenerationContext = (id: string | null) =>
	useQuery({
		queryKey: generationContextKey(id),
		queryFn: () => workOrderApi.generationContext(id as string),
		enabled: !!id,
	})

/**
 * Records what the order's artwork should be generated from.
 *
 * Not optimistic: the response carries server-assigned timestamps and the row is
 * read back to confirm what was stored, so patching the cache with a guess would
 * only be replaced a moment later by the truth.
 */
export const useSaveGenerationContext = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, input }: { id: string; input: GenerationContextInput }) =>
			workOrderApi.saveGenerationContext(id, input),
		onSuccess: (_data, { id }) => {
			void qc.invalidateQueries({ queryKey: generationContextKey(id) })
		},
	})
}

/** Root of the work-order-detail key family. Exported so the realtime bridge invalidates it. */
export const WORK_ORDER_DETAIL_KEY = 'work-order-detail'

/** Query key for one order's detail aggregate. */
export const workOrderDetailKey = (id: string) => [WORK_ORDER_DETAIL_KEY, id]

/**
 * The detail page's single read — order, hierarchy, resolved context, and
 * deliverables grouped by type with presigned thumbnails.
 *
 * `select` narrows the cached object without another request, which is how the
 * five deliverable boxes each render their own slice: five selectors over one
 * query rather than five near-identical `/files/discover` calls.
 */
export function useWorkOrderDetail<TSelected = WorkOrderDetail>(
	id: string | null,
	select?: (detail: WorkOrderDetail) => TSelected,
) {
	return useQuery({
		queryKey: workOrderDetailKey(id ?? ''),
		queryFn: () => workOrderApi.detail(id as string),
		enabled: !!id,
		select,
	})
}

/** Invalidate the board list, the single-order caches, and the audit log after a mutation. */
export const invalidateWorkOrders = (qc: ReturnType<typeof useQueryClient>) => {
	void qc.invalidateQueries({ queryKey: ['work-orders'] })
	void qc.invalidateQueries({ queryKey: ['work-order'] })
	// The detail page reads the aggregate, not `['work-order', id]` — without this
	// a status transition leaves the header and the substate stepper stale.
	void qc.invalidateQueries({ queryKey: [WORK_ORDER_DETAIL_KEY] })
	// Sending an order back clears the done tick on its change requests' unverified items.
	void qc.invalidateQueries({ queryKey: ['change-requests'] })
	// Not covered by the prefixes above: react-query matches key SEGMENTS by
	// equality, so `['work-order']` never reaches `['work-order-activity', id]`.
	// Every mutation here records an audit event, and without this the Activity
	// tab stayed on whatever it held until the page was reloaded.
	invalidateWorkOrderActivity(qc)
}

/**
 * Apply an update patch to a cached work order.
 *
 * A plain spread would not do: the patch's contract is that `null` clears a
 * field and `undefined` leaves it untouched, but an explicitly-present
 * `undefined` spreads straight over a real value. The edit form sends its whole
 * field set on every save, so those keys are routinely present and a naive merge
 * would blank whatever the user did not fill in.
 *
 * When the patch carries `links`, only the manual links are replaced with
 * `source: MANUAL`; external links mirrored from an external system are
 * preserved unchanged.
 */
const applyWorkOrderPatch = (workOrder: WorkOrder, patch: UpdateWorkOrderInput): WorkOrder => {
	const { aspectRatio, platform, pack, ...fields } = patch
	const set = Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined))
	if (patch.links !== undefined) {
		set.links = patchWorkOrderLinks(workOrder.links, patch.links)
	}
	return applyPackPatch({ ...workOrder, ...set }, { aspectRatio, platform, pack })
}

/**
 * Rewrite one order everywhere it is cached — the single-order read and every
 * board page — and return what was there before, for `onError` to put back.
 *
 * Both families matter on the detail page: it mounts the board list to resolve
 * the order's parent and children, so patching only the single-order read would
 * leave a stale assignee in the hierarchy beside a fresh one in the picker.
 */
const patchCachedWorkOrder = (
	qc: ReturnType<typeof useQueryClient>,
	id: string,
	transform: (workOrder: WorkOrder) => WorkOrder,
) => {
	const detailReads = qc.getQueriesData<WorkOrderDetailRead>({ queryKey: ['work-order', id] })
	for (const [key, read] of detailReads) {
		if (!read) continue
		qc.setQueryData<WorkOrderDetailRead>(key, { ...read, workOrder: transform(read.workOrder) })
	}

	const boardPages = qc.getQueriesData<WorkOrderBoardPage>({ queryKey: ['work-orders'] })
	for (const [key, page] of boardPages) {
		if (!page) continue
		qc.setQueryData<WorkOrderBoardPage>(key, {
			...page,
			workOrders: page.workOrders.map((w) => (w.workOrderId === id ? transform(w) : w)),
		})
	}

	return { detailReads, boardPages }
}

/** Put every snapshotted entry back, exactly as it was. */
const restoreCachedWorkOrders = (
	qc: ReturnType<typeof useQueryClient>,
	ctx: ReturnType<typeof patchCachedWorkOrder> | undefined,
) => {
	for (const [key, data] of ctx?.detailReads ?? []) qc.setQueryData(key, data)
	for (const [key, data] of ctx?.boardPages ?? []) qc.setQueryData(key, data)
}

interface UpdateVars {
	id: string
	patch: UpdateWorkOrderInput
}

/**
 * Patch a work order's details, tuple or assignees. Optimistic: the picker or
 * form shows the new value in the same frame as the interaction, rolling back if
 * the PATCH fails.
 *
 * The PATCH answers with the complete updated order, so `onSuccess` writes that
 * response into the cache directly — the user never waits on a read to see their
 * own change. The `onSettled` invalidation still runs, because `GET
 * /work-orders/{id}` also carries `workflow` and `workflowSnapshot`, which the
 * PATCH response does not, and because the error path needs reconciling too.
 *
 * @param workOrderId - The order this instance writes to, which scopes the mutation.
 * Repeated `mutate()` calls are independent lifecycles unless they share a scope, so
 * without one a slow write's rollback can land after a later write was confirmed and
 * restore a snapshot taken before either ran, silently reverting the newer value.
 * Scoping by order serialises writes to the SAME order while leaving other orders
 * parallel. Absent an id the instance has no order to race over, and they share a
 * scope of their own.
 */
export const useUpdateWorkOrder = (workOrderId?: string | null) => {
	const qc = useQueryClient()
	return useMutation({
		scope: { id: `work-order-update-${workOrderId ?? 'none'}` },
		mutationFn: ({ id, patch }: UpdateVars) => workOrderApi.update(id, patch),
		onMutate: async ({ id, patch }: UpdateVars) => {
			// Without cancelling, a read that started before the interaction can land
			// after the patch and revert the value the user just chose.
			await Promise.all([
				qc.cancelQueries({ queryKey: ['work-order', id] }),
				qc.cancelQueries({ queryKey: ['work-orders'] }),
			])
			return patchCachedWorkOrder(qc, id, (workOrder) => applyWorkOrderPatch(workOrder, patch))
		},
		onSuccess: (updated) => patchCachedWorkOrder(qc, updated.workOrderId, () => updated),
		onError: (_error, _vars, ctx) => restoreCachedWorkOrders(qc, ctx),
		onSettled: () => invalidateWorkOrders(qc),
	})
}

export const useCreateWorkOrder = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateWorkOrderInput) => workOrderApi.create(body),
		onSuccess: () => invalidateWorkOrders(qc),
	})
}

interface TransitionVars {
	id: string
	/** A status code of the order's assigned workflow - configuration, not a closed enum. */
	toStatus: string
}

/**
 * Transition a work order's status. Optimistic on the board's pages and the
 * order's own read alike: the card moves columns and the detail page's stage
 * changes in the same frame, rolling back if the request fails, and reconciling
 * on settle.
 */
export const useTransitionWorkOrder = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, toStatus }: TransitionVars) => workOrderApi.transition(id, toStatus),
		onMutate: async ({ id, toStatus }: TransitionVars) => {
			await Promise.all([
				qc.cancelQueries({ queryKey: ['work-order', id] }),
				qc.cancelQueries({ queryKey: ['work-orders'] }),
			])
			const now = new Date().toISOString()
			return patchCachedWorkOrder(qc, id, (workOrder) => ({ ...workOrder, status: toStatus, statusChangedAt: now }))
		},
		onError: (_e, _v, ctx) => restoreCachedWorkOrders(qc, ctx),
		onSettled: () => invalidateWorkOrders(qc),
	})
}

export const useArchiveWorkOrder = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => workOrderApi.archive(id),
		onSuccess: () => invalidateWorkOrders(qc),
	})
}

interface ImportMessagesVars {
	id: string
	fileId: string
	languageIso?: string
}

/**
 * Import carousel copy from an already-uploaded `.xlsx` (by `fileId`) into
 * classified messages (PR2). The caller uploads the spreadsheet through the
 * presigned-S3 file flow first, so this endpoint receives only the fileId. On
 * success invalidates BOTH the global messages list (`['messages']`) and the
 * per-order panel prefix (`['work-order-messages']`, keyed by orderCode) so the
 * newly-created copy appears in the work-order Messages card without a reload.
 * The server blocks on validation (row/card mismatch, missing copy) — the caller
 * surfaces the thrown error message.
 */
export const useImportMessages = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, fileId, languageIso }: ImportMessagesVars) =>
			workOrderApi.importMessages(id, fileId, languageIso),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [MESSAGES_KEY] })
			// The mutation knows only the workOrderId, not the orderCode the panel
			// keys on — invalidate the whole prefix so the affected order refetches.
			void qc.invalidateQueries({ queryKey: [WORK_ORDER_MESSAGES_KEY] })
			// The spreadsheet is classified as a file and each created message against the
			// order, so every read derived from classification is now a tuple behind.
			invalidateClassifiedReads(qc)
		},
	})
}
